import { forwardRef, useCallback } from 'react';
import { alpha, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import androidPhone from 'features/home/MigrationServiceCard/assets/android/phone.png';
import iosPhone from 'features/home/MigrationServiceCard/assets/ios/phone.png';
import { ServiceProvider } from 'features/entities/entitiesTypes';
import { jbColors } from 'app/theme/jbColors';
import { useDispatch, useSelector } from 'react-redux';
import { openExternalServiceStart } from 'features/extNavigation/extNavigationSlice';
import { selectOSType } from 'features/session/sessionSelectors';

type Props = {
  service: ServiceProvider;
};

export const MigrationCompleteServiceCard = forwardRef<HTMLLIElement, Props>(({ service }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const labelText = t('migration.tile.finished.label');
  const title1Text = t('migration.tile.finished.title1');
  const title2Text = t('migration.tile.finished.title2');
  const descriptionText = t('migration.tile.finished.description');

  const handleRedirect = useCallback(() => {
    dispatch(openExternalServiceStart(service));
  }, [dispatch, service]);

  const osType = useSelector(selectOSType);
  const phone = osType === 'iOS' ? iosPhone : androidPhone;

  return (
    <Box
      component="li"
      sx={{
        mb: 0.5,
        height: 150,
      }}
      ref={ref}
    >
      <Box
        sx={{
          height: 150,
          width: 150,
          background: (theme) =>
            `linear-gradient(99.47deg, ${theme.palette.common.white} 0%, #E4E5E6 100.72%)`,
          boxShadow: (theme) =>
            `0px 3px 5px -1px ${alpha(theme.palette.primary.main, 0.1)}, 0px 1px 18px 0px ${alpha(
              theme.palette.common.black,
              0.12
            )}, 0px 6px 10px 0px ${alpha(theme.palette.primary.main, 0.14)}`,
          borderRadius: '5px',
          p: (theme) => theme.spacing(2, 2.5, 3),
          '@media (min-width: 360px)': {
            width: 270,
          },
          '@media (min-width: 481px)': {
            width: 360,
          },
        }}
        onClick={handleRedirect}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            '@media (min-width: 481px)': {
              gap: 3,
            },
          }}
        >
          <Box
            sx={{
              '@media (min-width: 360px)': {
                flex: '0 0 170px',
              },
              '@media (min-width: 481px)': {
                flex: '0 0 200px',
              },
            }}
          >
            <Typography
              component="div"
              variant="caption"
              sx={{
                color: 'primary.main',
                fontWeight: 'fontWeightLight',
                textTransform: 'uppercase',
                letterSpacing: '0.08rem',
                lineHeight: 1.6667,
              }}
            >
              {labelText}
            </Typography>
            <Typography
              component="div"
              sx={{
                color: jbColors.support.royalBlue,
                fontWeight: 'fontWeightLight',
                fontSize: (theme) => theme.typography.pxToRem(16),
                lineHeight: 1.37,
                letterSpacing: '0.04rem',
                textTransform: 'uppercase',
                '@media (min-width: 360px)': {
                  fontSize: (theme) => theme.typography.pxToRem(20),
                  lineHeight: 1.3,
                },
              }}
            >
              {title1Text}
              <span style={{ fontWeight: '500' }}>{'JB ONE'}</span>
              {title2Text}
            </Typography>
            <Typography
              variant="body2"
              component="div"
              sx={{ mt: 1, fontWeight: 'fontWeightRegular', lineHeight: 1.42 }}
            >
              {descriptionText}
            </Typography>
          </Box>
          <Box
            sx={{
              mx: 'auto',
              height: 200,
              width: 84,
              position: 'relative',
              top: -35,
              right: 3,
              aspectRatio: 0.6383,
              backgroundImage: `url(${phone})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              '@media (min-width: 360px)': {
                height: 144,
                width: 60,
                top: -35,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
});
